import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Avatar,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
  Fade,
  alpha
} from '@mui/material';
import {
  Receipt as TransactionIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Image as ImageIcon,
  AccountBalanceWallet as WalletIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Store as StoreIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { fetchPendingTransactions, approveTransaction, rejectTransaction, fetchTransactionById } from '../transactionApi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../BranchContext';

const Transactions = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [notes, setNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // ✅ NEW: Image Slider State
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderZoomOpen, setSliderZoomOpen] = useState(false);
  const [zoomedSliderImage, setZoomedSliderImage] = useState('');
  
  const sliderImages = [
    '/transactions1.png'
  ];

  useEffect(() => {
    loadTransactions();
    const interval = setInterval(loadTransactions, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedBranch]);

  // ✅ NEW: Auto-play slider
  useEffect(() => {
    if (!sliderZoomOpen && !viewDialogOpen) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [sliderImages.length, sliderZoomOpen, viewDialogOpen]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const branchId = user.Permission === true ? null : (selectedBranch?.Id || user.BranchId);
      const data = await fetchPendingTransactions(branchId);
      setTransactions(data || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
      Swal.fire({
        title: t('Error'),
        text: t('TransactionLoadFail') || 'Failed to load transactions',
        icon: 'error',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Slider Navigation Handlers
  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  };

  // ✅ NEW: Slider Zoom Handlers
  const handleSliderZoomOpen = (image) => {
    setZoomedSliderImage(image);
    setSliderZoomOpen(true);
  };

  const handleSliderZoomClose = () => {
    setSliderZoomOpen(false);
  };

  const handleSliderZoomPrev = (e) => {
    e.stopPropagation();
    const currentIndex = sliderImages.indexOf(zoomedSliderImage);
    const prevIndex = (currentIndex - 1 + sliderImages.length) % sliderImages.length;
    setZoomedSliderImage(sliderImages[prevIndex]);
  };

  const handleSliderZoomNext = (e) => {
    e.stopPropagation();
    const currentIndex = sliderImages.indexOf(zoomedSliderImage);
    const nextIndex = (currentIndex + 1) % sliderImages.length;
    setZoomedSliderImage(sliderImages[nextIndex]);
  };

  // ✅ NEW: Slider Zoom Modal Component
  const SliderZoomModal = () => (
    <Dialog
      open={sliderZoomOpen}
      onClose={handleSliderZoomClose}
      maxWidth={false}
      fullScreen
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(56, 54, 54, 0.98)',
          m: 0,
          borderRadius: 0
        }
      }}
      sx={{
        zIndex: 10001,
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
    >
      <IconButton
        onClick={handleSliderZoomClose}
        sx={{
          position: 'fixed',
          right: { xs: 12, sm: 24 },
          top: { xs: 12, sm: 24 },
          zIndex: 10002,
          bgcolor: alpha(theme.palette.error.main, 0.9),
          color: 'white',
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          '&:hover': {
            bgcolor: theme.palette.error.dark,
            transform: 'scale(1.1)',
            boxShadow: '0 8px 24px rgba(211, 47, 47, 0.4)'
          },
          transition: 'all 0.3s',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
        }}
      >
        <CloseIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
      </IconButton>

      <Box
        sx={{
          position: 'fixed',
          top: { xs: 16, sm: 32 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10002,
          bgcolor: alpha(theme.palette.warning.main, 0.95),
          color: 'white',
          px: { xs: 3, sm: 4 },
          py: { xs: 1, sm: 1.5 },
          borderRadius: 4,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
        }}
      >
        <Typography
          variant="body1"
          fontWeight="bold"
          sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}
        >
          {sliderImages.indexOf(zoomedSliderImage) + 1} / {sliderImages.length}
        </Typography>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          height: '100vh',
          width: '100vw'
        }}
        onClick={handleSliderZoomClose}
      >
        <IconButton
          onClick={handleSliderZoomPrev}
          sx={{
            position: 'fixed',
            left: { xs: 12, sm: 32 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10002,
            bgcolor: alpha(theme.palette.common.white, 0.95),
            color: theme.palette.warning.main,
            width: { xs: 48, sm: 64 },
            height: { xs: 48, sm: 64 },
            '&:hover': {
              bgcolor: theme.palette.common.white,
              transform: 'translateY(-50%) scale(1.15)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
            },
            transition: 'all 0.3s',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: { xs: 24, sm: 32 }, ml: 1 }} />
        </IconButton>

        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            p: { xs: 2, sm: 4 }
          }}
        >
          <Box
            component="img"
            src={zoomedSliderImage}
            alt="Transaction Preview"
            sx={{
              maxWidth: '95vw',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              aspectRatio: '5/4',
              borderRadius: { xs: 1, sm: 2 },
              boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
              userSelect: 'none',
              pointerEvents: 'none'
            }}
          />
        </Box>

        <IconButton
          onClick={handleSliderZoomNext}
          sx={{
            position: 'fixed',
            right: { xs: 12, sm: 32 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10002,
            bgcolor: alpha(theme.palette.common.white, 0.95),
            color: theme.palette.warning.main,
            width: { xs: 48, sm: 64 },
            height: { xs: 48, sm: 64 },
            '&:hover': {
              bgcolor: theme.palette.common.white,
              transform: 'translateY(-50%) scale(1.15)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
            },
            transition: 'all 0.3s',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
        </IconButton>
      </DialogContent>

      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 24, sm: 40 },
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: { xs: 1.5, sm: 2 },
          zIndex: 10002,
          bgcolor: alpha(theme.palette.common.black, 0.7),
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderRadius: 5,
          backdropFilter: 'blur(10px)'
        }}
      >
        {sliderImages.map((img, index) => (
          <Box
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              setZoomedSliderImage(img);
            }}
            sx={{
              width: zoomedSliderImage === img ? { xs: 36, sm: 48 } : { xs: 12, sm: 14 },
              height: { xs: 12, sm: 14 },
              borderRadius: 7,
              bgcolor: zoomedSliderImage === img
                ? theme.palette.warning.main
                : alpha(theme.palette.common.white, 0.5),
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: zoomedSliderImage === img ? `3px solid ${theme.palette.common.white}` : 'none',
              '&:hover': {
                bgcolor: zoomedSliderImage === img
                  ? theme.palette.warning.light
                  : alpha(theme.palette.common.white, 0.8),
                transform: 'scale(1.2)'
              }
            }}
          />
        ))}
      </Box>
    </Dialog>
  );

  // ✅ NEW: Transaction Preview Slider Component
  const TransactionPreviewSlider = () => (
    <Card 
      elevation={6} 
      sx={{ 
        borderRadius: { xs: 2, sm: 3 },
        overflow: 'hidden',
        position: 'relative',
        bgcolor: alpha(theme.palette.warning.main, 0.03),
        height: isMobile ? 400 : isTablet ? 500 : 900,
        width: isMobile ? 300 : isTablet ? 400 : 1100,
        mx: 'auto',
        mt: 3
      }}
    >
      <Box sx={{ 
        bgcolor: 'warning.main', 
        color: 'white', 
        p: { xs: 1.5, sm: 2 },
        textAlign: 'center'
      }}>
        <Typography 
          variant="h6"
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.1rem' },
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          💳 {t('transactionPreview') || 'Transaction Management Guide'}
        </Typography>
      </Box>

      <Box 
        sx={{ 
          position: 'relative',
          width: '100%',
          height: isMobile ? 280 : isTablet ? 400 : 780,
          aspectRatio: '5/4',
          bgcolor: alpha(theme.palette.grey[200], 0.3)
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            p: { xs: 2, sm: 3 }
          }}
        >
          {sliderImages.map((image, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: currentSlide === index ? 1 : 0,
                transform: `translateX(${currentSlide === index ? 0 : currentSlide > index ? '-100%' : '100%'})`,
                transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: currentSlide === index ? 'none' : 'blur(4px)'
              }}
            >
              <Box
                component="img"
                src={image}
                alt={`Transaction Guide ${index + 1}`}
                onClick={() => handleSliderZoomOpen(image)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  aspectRatio: '5/4',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  boxShadow: currentSlide === index ? '0 10px 40px rgba(0,0,0,0.15)' : 'none',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              />
              {currentSlide === index && (
                <Box
                  onClick={() => handleSliderZoomOpen(image)}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: alpha(theme.palette.common.black, 0.6),
                    color: 'white',
                    borderRadius: '50%',
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      opacity: 1,
                      bgcolor: alpha(theme.palette.warning.main, 0.9)
                    }
                  }}
                >
                  <ZoomInIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <IconButton
          onClick={handlePrevSlide}
          sx={{
            position: 'absolute',
            left: { xs: 8, sm: 16 },
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: alpha(theme.palette.common.white, 0.9),
            '&:hover': {
              bgcolor: theme.palette.common.white,
              transform: 'translateY(-50%) scale(1.15)'
            },
            boxShadow: 3,
            zIndex: 2,
            transition: 'all 0.3s',
            width: { xs: 36, sm: 48 },
            height: { xs: 36, sm: 48 }
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: { xs: 16, sm: 20 }, ml: 0.5 }} />
        </IconButton>

        <IconButton
          onClick={handleNextSlide}
          sx={{
            position: 'absolute',
            right: { xs: 8, sm: 16 },
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: alpha(theme.palette.common.white, 0.9),
            '&:hover': {
              bgcolor: theme.palette.common.white,
              transform: 'translateY(-50%) scale(1.15)'
            },
            boxShadow: 3,
            zIndex: 2,
            transition: 'all 0.3s',
            width: { xs: 36, sm: 48 },
            height: { xs: 36, sm: 48 }
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
        </IconButton>

        <Tooltip title="Click to enlarge" arrow placement="top">
          <IconButton
            onClick={() => handleSliderZoomOpen(sliderImages[currentSlide])}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 12 },
              right: { xs: 8, sm: 12 },
              bgcolor: alpha(theme.palette.warning.main, 0.9),
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.warning.dark,
                transform: 'scale(1.1)'
              },
              boxShadow: 2,
              zIndex: 2,
              transition: 'all 0.3s',
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 }
            }}
            size="small"
          >
            <FullscreenIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 12, sm: 16 },
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 2,
            bgcolor: alpha(theme.palette.common.black, 0.3),
            px: 2,
            py: 1,
            borderRadius: 3,
            backdropFilter: 'blur(10px)'
          }}
        >
          {sliderImages.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentSlide(index)}
              sx={{
                width: currentSlide === index ? 32 : 10,
                height: 10,
                borderRadius: 5,
                bgcolor: currentSlide === index 
                  ? theme.palette.warning.main 
                  : alpha(theme.palette.common.white, 0.5),
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: currentSlide === index ? 2 : 0,
                border: currentSlide === index ? `2px solid ${theme.palette.common.white}` : 'none',
                '&:hover': {
                  bgcolor: currentSlide === index 
                    ? theme.palette.warning.light 
                    : alpha(theme.palette.common.white, 0.8),
                  transform: 'scale(1.2)'
                }
              }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        bgcolor: alpha(theme.palette.warning.main, 0.05),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.75rem', sm: '0.85rem' },
            fontWeight: 500
          }}
        >
          📸 {currentSlide + 1} / {sliderImages.length}
        </Typography>
        <Button
          size="small"
          startIcon={<ZoomInIcon />}
          onClick={() => handleSliderZoomOpen(sliderImages[currentSlide])}
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            textTransform: 'none'
          }}
        >
          اضغط لتكبير الصورة
        </Button>
      </Box>
    </Card>
  );

  const handleViewDetails = async (transaction) => {
    try {
      const details = await fetchTransactionById(transaction.Id);
      setSelectedTransaction(details);
      setViewDialogOpen(true);
    } catch (error) {
      Swal.fire({
        title: t('Error'),
        text: t('FailedToLoadDetails') || 'Failed to load details',
        icon: 'error',
        toast: true,
        position: 'top-end',
        timer: 3000
      });
    }
  };

  const handleApprove = async (transactionId) => {
    setViewDialogOpen(false);
    
    const result = await Swal.fire({
      title: t('ApproveTransaction') || 'Approve Transaction?',
      html: '<div style="text-align: left; padding: 10px;"><p>' + 
            (t('ApproveTransactionConfirm') || 'This will mark the payment as verified and proceed with the order') +
            '</p>' + (notes ? '<p style="margin-top: 10px;"><strong>Notes:</strong> ' + notes + '</p>' : '') + '</div>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2e7d32',
      cancelButtonColor: '#757575',
      confirmButtonText: '<i class="fas fa-check"></i> ' + (t('Yes_Approve') || 'Yes, Approve'),
      cancelButtonText: '<i class="fas fa-times"></i> ' + t('Cancel'),
      customClass: {
        popup: 'swal-wide',
        confirmButton: 'swal-button-success',
        cancelButton: 'swal-button-cancel'
      },
      didOpen: () => {
        const container = document.querySelector('.swal2-container');
        if (container) container.style.zIndex = 9999;
      }
    });

    if (result.isConfirmed) {
      setActionLoading(true);
      try {
        await approveTransaction(transactionId, null, notes, user.Id, user.Name);
        await Swal.fire({
          title: t('Approved'),
          text: t('TransactionApproved') || 'Transaction approved successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          didOpen: () => {
            const container = document.querySelector('.swal2-container');
            if (container) container.style.zIndex = 9999;
          }
        });
        setNotes('');
        await loadTransactions();
      } catch (error) {
        console.error('Approval error:', error);
        Swal.fire({
          title: t('Error'),
          text: error.response?.data?.message || t('OperationFailed'),
          icon: 'error',
          didOpen: () => {
            const container = document.querySelector('.swal2-container');
            if (container) container.style.zIndex = 9999;
          }
        });
      } finally {
        setActionLoading(false);
      }
    } else {
      setViewDialogOpen(true);
    }
  };

  const handleRejectClick = (transaction) => {
    setSelectedTransaction(transaction);
    setViewDialogOpen(false);
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      Swal.fire({
        title: t('Error'),
        text: t('RejectionReasonRequired') || 'Please provide a rejection reason',
        icon: 'error',
        toast: true,
        position: 'top-end',
        timer: 3000
      });
      return;
    }

    setRejectDialogOpen(false);
    
    const result = await Swal.fire({
      title: t('RejectTransaction') || 'Reject Transaction?',
      html: '<div style="text-align: left; padding: 10px;"><p><strong>Reason:</strong> ' + 
            rejectionReason + '</p>' + 
            (notes ? '<p style="margin-top: 10px;"><strong>Notes:</strong> ' + notes + '</p>' : '') + 
            '</div>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#757575',
      confirmButtonText: '<i class="fas fa-ban"></i> ' + (t('ConfirmRejection') || 'Confirm Rejection'),
      cancelButtonText: '<i class="fas fa-times"></i> ' + t('Cancel'),
      didOpen: () => {
        const container = document.querySelector('.swal2-container');
        if (container) container.style.zIndex = 9999;
      }
    });

    if (result.isConfirmed) {
      setActionLoading(true);
      try {
        await rejectTransaction(selectedTransaction.Id, null, rejectionReason, notes, user.Id, user.Name);
        await Swal.fire({
          title: t('Rejected'),
          text: t('TransactionRejected') || 'Transaction rejected',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          didOpen: () => {
            const container = document.querySelector('.swal2-container');
            if (container) container.style.zIndex = 9999;
          }
        });
        setRejectionReason('');
        setNotes('');
        await loadTransactions();
      } catch (error) {
        console.error('Rejection error:', error);
        Swal.fire({
          title: t('Error'),
          text: error.response?.data?.message || t('OperationFailed'),
          icon: 'error',
          didOpen: () => {
            const container = document.querySelector('.swal2-container');
            if (container) container.style.zIndex = 9999;
          }
        });
      } finally {
        setActionLoading(false);
      }
    } else {
      setRejectDialogOpen(true);
    }
  };

  const getCurrencyDisplay = (transaction) => {
    if (transaction.FormattedAmount) {
      return transaction.FormattedAmount;
    }
    const symbol = transaction.CurrencySymbol || '';
    const amount = transaction.Amount || 0;
    return symbol + amount;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <SliderZoomModal />
      
      <Card elevation={3}>
        <CardHeader
          title={
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              alignItems={{ xs: 'flex-start', sm: 'center' }} 
              spacing={{ xs: 1, sm: 2 }}
            >
              <TransactionIcon 
                color="primary" 
                sx={{ fontSize: { xs: 32, sm: 40 } }} 
              />
              <Box>
                <Typography 
                  variant="h5" 
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {t('PendingTransactions') || 'Pending Payment Verifications'}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {t('ReviewWalletPayments') || 'Review and approve customer wallet payments'}
                </Typography>
              </Box>
            </Stack>
          }
          action={
            <Badge badgeContent={transactions.length} color="warning" max={99}>
              <Chip 
                label={t('Pending')}
                color="warning" 
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  fontWeight: 'bold', 
                  fontSize: { xs: '0.75rem', sm: '0.9rem' }, 
                  px: { xs: 0.5, sm: 1 }
                }}
              />
            </Badge>
          }
          sx={{ 
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderBottom: 2,
            borderColor: 'primary.main',
            pb: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 }
          }}
        />
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 6, sm: 8 } }}>
              <CircularProgress size={isMobile ? 40 : 60} />
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              variant="outlined" 
              sx={{ 
                maxHeight: { xs: 'calc(100vh - 200px)', sm: 600 },
                overflowX: 'auto'
              }}
            >
              <Table stickyHeader size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        bgcolor: 'primary.light', 
                        color: 'white',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        p: { xs: 1, sm: 2 }
                      }}
                    >
                      {t('TransactionID') || 'Transaction ID'}
                    </TableCell>
                    {!isMobile && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'primary.light', 
                          color: 'white',
                          fontSize: '0.875rem'
                        }}
                      >
                        {t('OrderID') || 'Order ID'}
                      </TableCell>
                    )}
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        bgcolor: 'primary.light', 
                        color: 'white',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        p: { xs: 1, sm: 2 }
                      }}
                    >
                      {t('Customer')}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        bgcolor: 'primary.light', 
                        color: 'white',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        p: { xs: 1, sm: 2 }
                      }}
                    >
                      {t('Amount')}
                    </TableCell>
                    {!isMobile && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'primary.light', 
                          color: 'white',
                          fontSize: '0.875rem'
                        }}
                      >
                        {t('Wallet')}
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'primary.light', 
                          color: 'white',
                          fontSize: '0.875rem'
                        }}
                      >
                        {t('Date')}
                      </TableCell>
                    )}
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 'bold', 
                        bgcolor: 'primary.light', 
                        color: 'white',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        p: { xs: 1, sm: 2 }
                      }}
                    >
                      {t('Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((txn) => (
                    <TableRow 
                      key={txn.Id} 
                      hover 
                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                        <Tooltip title="Click to view details">
                          <Typography 
                            variant="body2" 
                            fontWeight="bold" 
                            color="primary.main"
                            sx={{ 
                              cursor: 'pointer',
                              fontSize: { xs: '0.7rem', sm: '0.875rem' }
                            }}
                            onClick={() => handleViewDetails(txn)}
                          >
                            {isMobile ? txn.TransactionId.substring(0, 8) + '...' : txn.TransactionId}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Chip label={txn.OrderId} size="small" variant="outlined" />
                        </TableCell>
                      )}
                      <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar 
                            sx={{ 
                              width: { xs: 28, sm: 36 }, 
                              height: { xs: 28, sm: 36 }, 
                              bgcolor: 'primary.main',
                              fontSize: { xs: '0.75rem', sm: '1rem' }
                            }}
                          >
                            {txn.User?.FullName?.charAt(0) || 'U'}
                          </Avatar>
                          <Box>
                            <Typography 
                              variant="body2" 
                              fontWeight="500"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              {isMobile 
                                ? (txn.User?.FullName?.split(' ')[0] || '-')
                                : (txn.User?.FullName || '-')
                              }
                            </Typography>
                            {!isMobile && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                {txn.User?.Phone || ''}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                        <Chip 
                          label={getCurrencyDisplay(txn)}
                          color="success" 
                          size="small"
                          sx={{ 
                            fontWeight: 'bold', 
                            fontSize: { xs: '0.7rem', sm: '0.85rem' }
                          }}
                        />
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {txn.Wallet?.ImageUrl && (
                              <Avatar src={txn.Wallet.ImageUrl} sx={{ width: 28, height: 28 }} />
                            )}
                            <Box>
                              <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.85rem' }}>
                                {txn.WalletName}
                              </Typography>
                              <Chip 
                                label={txn.WalletNumber} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Box>
                          </Stack>
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {new Date(txn.CreatedAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ p: { xs: 0.5, sm: 1 } }}>
                        <Stack 
                          direction={isMobile ? "column" : "row"} 
                          spacing={0.5} 
                          justifyContent="flex-end"
                        >
                          <Tooltip title="View Details">
                            <IconButton 
                              color="info" 
                              onClick={() => handleViewDetails(txn)} 
                              size="small"
                              sx={{ '&:hover': { bgcolor: 'info.light' } }}
                            >
                              <ViewIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <IconButton 
                              color="success" 
                              onClick={() => handleApprove(txn.Id)} 
                              size="small"
                              sx={{ '&:hover': { bgcolor: 'success.light' } }}
                            >
                              <ApproveIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton 
                              color="error" 
                              onClick={() => handleRejectClick(txn)} 
                              size="small"
                              sx={{ '&:hover': { bgcolor: 'error.light' } }}
                            >
                              <RejectIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 4 : 7} align="center">
                        <Box sx={{ py: { xs: 4, sm: 6 } }}>
                          <TransactionIcon 
                            sx={{ 
                              fontSize: { xs: 40, sm: 60 }, 
                              color: 'text.disabled', 
                              mb: 2 
                            }} 
                          />
                          <Typography 
                            variant="h6" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                          >
                            {t('NoPendingTransactions') || 'No pending transactions'}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.disabled"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            {t('AllCaughtUp') || 'All caught up! New transactions will appear here.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ✅ NEW: Transaction Guide Slider Below Table */}
      <TransactionPreviewSlider />
            {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '100px', sm: '80px', md: '100px' }
          }
        }}
        PaperProps={{ 
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' },
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'primary.light', 
            color: 'white', 
            borderRadius: isMobile ? 0 : '12px 12px 0 0',
            p: { xs: 2, sm: 2.5 }
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <TransactionIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {t('TransactionDetails') || 'Transaction Details'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
          {selectedTransaction && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <Alert severity="info" icon={<WalletIcon />} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    <strong>{selectedTransaction.TransactionId}</strong> - {selectedTransaction.Title}
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={{ xs: 2, sm: 2.5 }}>
                  <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, borderLeft: 3, borderColor: 'primary.main' }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {t('OrderID')}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      #{selectedTransaction.OrderId}
                    </Typography>
                  </Paper>

                  <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, borderLeft: 3, borderColor: 'success.main' }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {t('Amount')}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography 
                        variant="h5" 
                        color="success.main" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      >
                        {getCurrencyDisplay(selectedTransaction)}
                      </Typography>
                      {selectedTransaction.CurrencyName && (
                        <Chip 
                          label={selectedTransaction.CurrencyName} 
                          size="small" 
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      )}
                    </Stack>
                  </Paper>

                  <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default' }}>
                    <Stack spacing={{ xs: 1, sm: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {selectedTransaction.Wallet?.ImageUrl ? (
                          <Avatar 
                            src={selectedTransaction.Wallet.ImageUrl} 
                            sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }} 
                          />
                        ) : (
                          <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, bgcolor: 'primary.main' }}>
                            <WalletIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </Avatar>
                        )}
                        <Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {t('Wallet')}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            fontWeight="600"
                            sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
                          >
                            {selectedTransaction.WalletName}
                          </Typography>
                        </Box>
                      </Stack>
                      <Divider />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          {t('WalletNumber')}
                        </Typography>
                        <Chip 
                          label={selectedTransaction.WalletNumber} 
                          size="small" 
                          color="primary"
                          sx={{ fontWeight: 'bold', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      </Stack>
                    </Stack>
                  </Paper>

                  <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {t('Customer')}
                    </Typography>
                    <Stack spacing={1} mt={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="action" sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {selectedTransaction.User?.FullName || '-'}
                        </Typography>
                      </Stack>
                      {selectedTransaction.User?.Phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon fontSize="small" color="action" sx={{ fontSize: { xs: 16, sm: 18 } }} />
                          <Typography 
                            variant="body2"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            {selectedTransaction.User.Phone}
                          </Typography>
                        </Stack>
                      )}
                      {selectedTransaction.User?.Email && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EmailIcon fontSize="small" color="action" sx={{ fontSize: { xs: 16, sm: 18 } }} />
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {selectedTransaction.User.Email}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Paper>

                  {selectedTransaction.Branch && (
                    <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StoreIcon color="action" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        <Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {t('Branch')}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            fontWeight="500"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            {selectedTransaction.Branch.Name}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
                  <Stack spacing={1} height="100%">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {t('PaymentProof') || 'Payment Proof'}
                      </Typography>
                      {selectedTransaction.TransactionImageUrl && (
                        <Tooltip title="View full size">
                          <IconButton 
                            size="small" 
                            onClick={() => setImagePreviewOpen(true)}
                            color="primary"
                          >
                            <ZoomInIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <Divider />
                    {selectedTransaction.TransactionImageUrl ? (
                      <Box
                        component="img"
                        src={selectedTransaction.TransactionImageUrl}
                        alt="Payment Proof"
                        onClick={() => setImagePreviewOpen(true)}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: { xs: 250, sm: 450 },
                          objectFit: 'contain',
                          border: '2px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: '#f9f9f9',
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: 3
                          }
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          p: { xs: 4, sm: 6 }, 
                          textAlign: 'center', 
                          bgcolor: '#f5f5f5', 
                          borderRadius: 2,
                          border: '2px dashed',
                          borderColor: 'divider'
                        }}
                      >
                        <ImageIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#ccc', mb: 2 }} />
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {t('NoImageUploaded') || 'No payment proof image'}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <TextField
                  fullWidth
                  label={t('Notes') || 'Admin Notes (Optional)'}
                  multiline
                  rows={isMobile ? 2 : 3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('AddNotesForApproval') || 'Add any notes about this transaction...'}
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    mt: 2,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.85rem', sm: '0.9rem' }
                    }
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions 
          sx={{ 
            px: { xs: 2, sm: 3 }, 
            py: { xs: 1.5, sm: 2 }, 
            bgcolor: 'background.default',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}
        >
          <Button 
            onClick={() => setViewDialogOpen(false)} 
            color="inherit"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('Close')}
          </Button>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<RejectIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            onClick={() => handleRejectClick(selectedTransaction)}
            disabled={actionLoading}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('Reject')}
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            startIcon={actionLoading ? <CircularProgress size={20} /> : <ApproveIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            onClick={() => handleApprove(selectedTransaction.Id)}
            disabled={actionLoading}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              minWidth: { xs: 'auto', sm: 120 },
              fontSize: { xs: '0.85rem', sm: '0.9rem' }
            }}
          >
            {actionLoading ? t('Processing') : t('Approve')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog 
        open={rejectDialogOpen} 
        onClose={() => setRejectDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '100px', sm: '80px', md: '100px' }
          }
        }}
        PaperProps={{ 
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' },
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'error.light', 
            color: 'white',
            p: { xs: 2, sm: 2.5 }
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <RejectIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {t('RejectTransaction') || 'Reject Transaction'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Alert severity="warning" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {t('RejectWarning') || 'Please provide a clear reason for rejecting this payment'}
            </Alert>
            <TextField
              fullWidth
              label={t('RejectionReason') || 'Reason for Rejection'}
              multiline
              rows={isMobile ? 3 : 4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              required
              placeholder={t('ExplainWhyRejecting') || 'Please explain why you are rejecting this payment...'}
              error={!rejectionReason.trim()}
              helperText={!rejectionReason.trim() ? t('Required') : ''}
              size={isMobile ? "small" : "medium"}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.85rem', sm: '0.9rem' }
                }
              }}
            />
            <TextField
              fullWidth
              label={t('AdditionalNotes') || 'Additional Notes (Optional)'}
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('OptionalNotes') || 'Any additional information...'}
              size={isMobile ? "small" : "medium"}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.85rem', sm: '0.9rem' }
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions 
          sx={{ 
            px: { xs: 2, sm: 3 }, 
            py: { xs: 1.5, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}
        >
          <Button 
            onClick={() => setRejectDialogOpen(false)} 
            color="inherit"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('Cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleRejectSubmit}
            disabled={!rejectionReason.trim() || actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <RejectIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {actionLoading ? t('Processing') : (t('ConfirmRejection') || 'Confirm Rejection')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '60px', sm: '40px', md: '40px' }
          }
        }}
        PaperProps={{ 
          sx: { 
            bgcolor: 'black',
            borderRadius: isMobile ? 0 : 2,
            maxHeight: { xs: '100vh', sm: 'calc(100vh - 80px)', md: 'calc(100vh - 80px)' }
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedTransaction?.TransactionImageUrl && (
            <Box
              component="img"
              src={selectedTransaction.TransactionImageUrl}
              alt="Payment Proof Full Size"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: { xs: '100vh', sm: '90vh' },
                objectFit: 'contain'
              }}
            />
          )}
          <IconButton
            onClick={() => setImagePreviewOpen(false)}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 10 },
              right: { xs: 8, sm: 10 },
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'white' },
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 }
            }}
          >
            <RejectIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogContent>
      </Dialog>

      <style>
        {`
          .swal2-container {
            z-index: 9999 !important;
          }
          .swal-wide {
            width: 500px !important;
          }
          @media (max-width: 600px) {
            .swal-wide {
              width: 95vw !important;
            }
          }
          .swal-button-success {
            font-weight: 600 !important;
          }
          .swal-button-cancel {
            font-weight: 600 !important;
          }
        `}
      </style>
    </Box>
  );
};

export default Transactions;